import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Alert, Box, Button, Divider, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { Money, formatCRC } from "@despejo/domain";
import { computeIva, reconcile, type Candidate } from "@despejo/tax-engine";
import { StatusChip } from "@despejo/ui";
import { ledgerToIvaInput, type LedgerTx } from "../lib/ivaMapping.js";
import { ivaBoxes, type IvaRefInfo } from "../lib/ivaDrill.js";
import { DrillFigure, useDrillDown, type DrillSpec } from "./DrillDown.js";
import { parseAmount } from "../lib/csv.js";
import { must, supabase } from "../lib/supabase.js";
import type { ObligationRow } from "../lib/types.js";

const FIELDS: { key: "totalDebit" | "totalCredit" | "payable" | "creditCarryForward"; label: string }[] = [
  { key: "totalDebit", label: "Débito fiscal (IVA de ventas)" },
  { key: "totalCredit", label: "Crédito fiscal aplicable" },
  { key: "payable", label: "Impuesto por pagar" },
  { key: "creditCarryForward", label: "Saldo a favor" },
];

/** Decreto 44739-H art. 9: electronic document types. */
const DOC_TYPE: Record<string, string> = {
  "01": "Factura electrónica",
  "02": "Nota de débito",
  "03": "Nota de crédito",
  "04": "Tiquete electrónico",
  "08": "Factura electrónica de compra",
  "09": "Factura electrónica de exportación",
};
const ACCEPTANCE: Record<string, string> = {
  NOT_REQUIRED: "sin aceptación requerida",
  PENDING: "aceptación pendiente",
  ACCEPTED: "aceptado",
  PARTIALLY_ACCEPTED: "aceptado parcialmente",
  REJECTED: "rechazado",
};

async function sha256(s: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(s));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Plan §15.1/§15.3: calculate from the ledger, capture the TRIBU-CR draft, reconcile box by box. */
export function IvaCalculation({ row, onSaved }: { row: ObligationRow; onSaved: () => void }) {
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [retentions, setRetentions] = useState("0");
  const [prior, setPrior] = useState("0");
  const [msg, setMsg] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const drill = useDrillDown();

  const ledger = useQuery({
    queryKey: ["iva-ledger", row.taxpayer_id, row.period_key],
    queryFn: async () => {
      const txs = must(
        await supabase
          .from("transactions")
          .select("id, direction, document_date, description, counterparty_name, has_electronic_receipt, business_use, receipt_id, subtotal:subtotal::text, tax_amount:tax_amount::text, total_crc:total_crc::text, exchange_rate:exchange_rate::text, business_pct:business_pct::text")
          .eq("taxpayer_id", row.taxpayer_id)
          .eq("tax_period", row.period_key)
          .is("excluded_reason", null),
      ) as (Omit<LedgerTx, "lines" | "receipt_acceptance" | "receipt_doc_type"> & { receipt_id: string | null; document_date: string; counterparty_name: string | null })[];
      const ids = txs.map((t) => t.id);
      const lines = ids.length
        ? (must(await supabase.from("transaction_lines").select("transaction_id, line_no, description, exemption, tax_category, taxable_base:taxable_base::text, iva_rate:iva_rate::text, iva_amount:iva_amount::text").in("transaction_id", ids)) as (LedgerTx["lines"][number] & { transaction_id: string; description: string | null })[])
        : [];
      const recIds = txs.map((t) => t.receipt_id).filter(Boolean) as string[];
      const recs = recIds.length
        ? (must(await supabase.from("electronic_receipts").select("id, acceptance_status, acceptance_due_date, doc_type, clave, consecutive").in("id", recIds)) as { id: string; acceptance_status: string; acceptance_due_date: string | null; doc_type: string; clave: string; consecutive: string | null }[])
        : [];
      const byRec = new Map(recs.map((r) => [r.id, r]));
      // Drill-down labels: every ledger reference the engine can trace back to (transaction, line, receipt).
      const refs = new Map<string, IvaRefInfo>();
      for (const t of txs) {
        const rec = t.receipt_id ? byRec.get(t.receipt_id) : undefined;
        const receipt = rec
          ? `${DOC_TYPE[rec.doc_type] ?? `Comprobante ${rec.doc_type}`} ${rec.consecutive ?? `…${rec.clave.slice(-8)}`} · ${ACCEPTANCE[rec.acceptance_status] ?? rec.acceptance_status}`
          : t.has_electronic_receipt
            ? null
            : "Sin comprobante electrónico";
        const base = t.description ?? (t.direction === "INCOME" ? "Venta" : "Compra");
        const detail = [t.counterparty_name, receipt, t.exchange_rate ? `tipo de cambio ${t.exchange_rate}` : null].filter(Boolean).join(" · ") || null;
        refs.set(t.id, { date: t.document_date, label: base, detail });
        for (const l of lines.filter((x) => x.transaction_id === t.id)) {
          refs.set(`${t.id}#${l.line_no}`, { date: t.document_date, label: l.description ? `${base} · ${l.description}` : `${base} · línea ${l.line_no}`, detail });
        }
      }
      const txList = txs.map<LedgerTx>((t) => ({
        ...t,
        lines: lines.filter((l) => l.transaction_id === t.id),
        receipt_acceptance: t.receipt_id ? (byRec.get(t.receipt_id)?.acceptance_status ?? null) : null,
        receipt_acceptance_due: t.receipt_id ? (byRec.get(t.receipt_id)?.acceptance_due_date ?? null) : null,
        receipt_doc_type: t.receipt_id ? (byRec.get(t.receipt_id)?.doc_type ?? null) : null,
      }));
      return { txs: txList, refs };
    },
  });

  const calc = useMemo(() => {
    if (!ledger.data) return null;
    const { input, issues } = ledgerToIvaInput(row.period_key, ledger.data.txs, { provisionalProportionPct: null, cardRetentions: parseAmount(retentions) ?? "0", priorCreditBalance: parseAmount(prior) ?? "0" });
    const result = computeIva(input);
    return { input, issues, result, boxes: ivaBoxes(input, result, ledger.data.refs) };
  }, [ledger.data, row.period_key, retentions, prior]);

  const recon = useMemo(() => {
    if (!calc) return null;
    const draftVals = Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, parseAmount(v) ?? "0"]).filter(([, v]) => v !== null));
    if (!Object.keys(draftVals).length) return null;
    const platform = Object.fromEntries(FIELDS.map((f) => [f.key, calc.result[f.key]]));
    // Provable explanations: IVA the platform refused but Hacienda's draft likely credits.
    const cands: Candidate[] = calc.result.trace
      .filter((t) => t.bucket === "credit:not_allowed")
      .map((t) => ({ cause: /aceptación/.test(t.note ?? "") ? "UNACCEPTED_INVOICE" : /personal/i.test(t.note ?? "") ? "PERSONAL_PURCHASE" : "OTHER", field: "totalCredit", amount: Money.of(t.amount).neg().toString(), refs: [t.ref], note: t.note ?? "" }));
    return reconcile(Object.fromEntries(Object.entries(platform).filter(([k]) => k in draftVals)), draftVals, cands);
  }, [calc, draft]);

  const blocking = (calc?.issues.length ?? 0) + (calc?.result.exceptions.filter((e) => e.code === "MISSING_RATE" || e.code === "PRORRATA_UNKNOWN").length ?? 0);

  const save = async () => {
    if (!calc) return;
    setMsg(null);
    try {
      let snapshotId: string | null = null;
      if (recon) {
        const snap = must(
          await supabase
            .from("prefill_snapshots")
            .insert({ organization_id: row.organization_id, taxpayer_id: row.taxpayer_id, obligation_id: row.id, form: "150", fields: Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, parseAmount(v)])) })
            .select("id")
            .single(),
        ) as { id: string };
        snapshotId = snap.id;
      }
      const inputsJson = JSON.stringify(calc.input);
      const ready = blocking === 0 && (recon?.readyToFile ?? false);
      must(
        await supabase.from("calculations").insert({
          organization_id: row.organization_id,
          taxpayer_id: row.taxpayer_id,
          obligation_id: row.id,
          engine: calc.result.engine,
          inputs_sha256: await sha256(inputsJson),
          inputs: calc.input,
          result: calc.result,
          prefill_snapshot_id: snapshotId,
          reconciliation: recon,
          ready_to_file: ready,
        }),
      );
      must(
        await supabase
          .from("taxpayer_obligations")
          .update({ expected_amount: calc.result.payable, expected_amount_source: "CALCULATED", preparation_status: ready ? "READY_TO_FILE" : blocking ? "DATA_INCOMPLETE" : "READY_FOR_REVIEW" })
          .eq("id", row.id),
      );
      setMsg({ tone: "success", text: ready ? "Cálculo guardado: listo para presentar." : "Cálculo guardado. Aún hay diferencias o datos por resolver." });
      onSaved();
    } catch (e) {
      setMsg({ tone: "error", text: (e as Error).message });
    }
  };

  if (ledger.isLoading) return <Typography variant="body2">Cargando movimientos…</Typography>;
  if (ledger.error) return <Alert severity="error">{(ledger.error as Error).message}</Alert>;
  if (!calc) return null;
  const r = calc.result;
  const box = (key: string) => calc.boxes.find((b) => b.key === key)!;
  const reconSpec = (key: (typeof FIELDS)[number]["key"]): DrillSpec => {
    if (key === "totalDebit" || key === "totalCredit") return box(key).spec;
    // Payable and carry-forward: the same signed balance proof, titled as the field.
    return { ...box("balance").spec, title: FIELDS.find((f) => f.key === key)!.label };
  };
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Calculado con {r.engine} a partir de {ledger.data?.txs.length ?? 0} movimiento(s) del periodo. Toque cualquier monto para ver los movimientos y comprobantes que lo forman.
      </Typography>
      {(calc.issues.length > 0 || r.exceptions.length > 0) && (
        <Alert severity="warning">
          {calc.issues.map((i) => (
            <div key={i.ref}>{i.message}</div>
          ))}
          {r.exceptions.map((e, n) => (
            <div key={n}>{e.message}</div>
          ))}
        </Alert>
      )}
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small" aria-label="Formulario 150 calculado">
          <TableHead>
            <TableRow>
              <TableCell>Concepto</TableCell>
              <TableCell align="right">Base</TableCell>
              <TableCell align="right">IVA</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {calc.boxes.map((b) => (
              <TableRow key={b.key}>
                <TableCell sx={b.strong ? { fontWeight: 600 } : undefined}>{b.label}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  {b.column === "base" && <DrillFigure amount={b.amount} label={b.label} onOpen={() => drill.open(b.spec)} />}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap", ...(b.strong ? { fontWeight: 600 } : {}) }}>
                  {b.column === "iva" && <DrillFigure amount={b.amount} label={b.label} onOpen={() => drill.open(b.spec)} />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Stack direction="row" spacing={2}>
        <TextField label="Retenciones de tarjeta (₡)" value={retentions} onChange={(e) => setRetentions(e.target.value)} size="small" />
        <TextField label="Saldo a favor anterior (₡)" value={prior} onChange={(e) => setPrior(e.target.value)} size="small" />
      </Stack>
      <Divider />
      <Typography variant="subtitle2">Borrador de TRIBU-CR (formulario 150)</Typography>
      <Typography variant="caption" color="text.secondary">
        Copie los montos del borrador precargado de Hacienda para comparar casilla por casilla.
      </Typography>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Concepto</TableCell>
              <TableCell align="right">Despejo</TableCell>
              <TableCell align="right">TRIBU-CR</TableCell>
              <TableCell>Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {FIELDS.map((f) => {
              const d = recon?.fields.find((x) => x.field === f.key);
              return (
                <TableRow key={f.key}>
                  <TableCell>{f.label}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <DrillFigure amount={r[f.key]} label={f.label} onOpen={() => drill.open(reconSpec(f.key))} />
                  </TableCell>
                  <TableCell align="right">
                    <TextField size="small" variant="standard" value={draft[f.key] ?? ""} onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })} inputMode="decimal" sx={{ width: 140 }} />
                  </TableCell>
                  <TableCell>
                    {d?.status === "MATCH" && <StatusChip tone="success" icon="check" label="Coincide" />}
                    {d?.status === "EXPLAINED" && <StatusChip tone="success" icon="check_circle" label={`Explicada: ${d.explainedBy.map((e) => e.note).join("; ")}`} />}
                    {d?.status === "UNEXPLAINED" && <StatusChip tone="error" icon="error" label={`Diferencia ${formatCRC(Money.of(d.unexplained))}`} />}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      {msg && <Alert severity={msg.tone}>{msg.text}</Alert>}
      <Button variant="contained" onClick={save} sx={{ alignSelf: "flex-start" }}>
        Guardar cálculo
      </Button>
      {drill.dialog}
    </Stack>
  );
}
